import React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import { cn } from 'lib/utils';
import { isExternalEditorLayer } from 'lib/externalEditorLayer';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  showCloseButton?: boolean;
  closeOnOverlayClick?: boolean;
  closeOnEscape?: boolean;
}

const SIZES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: 'max-w-sm',
  md: 'max-w-lg',
  lg: 'max-w-2xl',
  xl: 'max-w-4xl',
  full: 'max-w-[calc(100%-2rem)]',
};

/** Radix-based so it nests correctly: a plain body-portal modal inherits the parent's `pointer-events: none`. */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  size = 'md',
  showCloseButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
}) => {
  return (
    <DialogPrimitive.Root
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay
          className={cn(
            'fixed inset-0 z-50 bg-[rgba(17,24,39,0.5)]',
            'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          )}
        />
        {/* Centering layer is click-through so an outside click reaches the
            overlay (Radix maps that to a dismiss); the content re-enables
            pointer events for itself. Height is 100dvh, not the inset-0
            default: on mobile Safari a fixed layer spans the *large* viewport
            (the one behind the collapsing toolbars), so the modal centred in
            it — and its bottom actions — sat under the browser chrome. */}
        <div className="pointer-events-none fixed inset-0 z-50 flex h-[100dvh] items-center justify-center p-4">
          <DialogPrimitive.Content
            // When there's no Description, tell Radix so explicitly to silence
            // its dev-only "missing description" warning; when there is one,
            // let Radix auto-wire aria-describedby to it.
            {...(description ? {} : { 'aria-describedby': undefined })}
            onEscapeKeyDown={(e) => {
              // MathLive swallows Escape to close its own keyboard; letting it
              // bubble would close the whole dialog instead.
              if (!closeOnEscape || isExternalEditorLayer(document.activeElement)) {
                e.preventDefault();
              }
            }}
            onPointerDownOutside={(e) => {
              if (!closeOnOverlayClick || isExternalEditorLayer(e.detail.originalEvent.target)) {
                e.preventDefault();
              }
            }}
            onInteractOutside={(e) => {
              if (!closeOnOverlayClick || isExternalEditorLayer(e.detail.originalEvent.target)) {
                e.preventDefault();
              }
            }}
            className={cn(
              // rounded-lg matches Dialog/AlertDialog — the two overlay
              // primitives must not present two different corner shapes.
              'pointer-events-auto relative flex max-h-[calc(100dvh-2rem)] w-full flex-col overflow-hidden rounded-lg border border-border bg-card text-foreground shadow-lg',
              'data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              SIZES[size],
            )}
          >
            {/* Radix requires a Title for accessibility; render an sr-only one
                when the caller supplies no visible title. */}
            {!title && <DialogPrimitive.Title className="sr-only">Dialog</DialogPrimitive.Title>}

            {(title || showCloseButton) && (
              <div className="flex shrink-0 items-start justify-between gap-4 border-b border-border p-4 sm:p-6">
                <div className="space-y-1">
                  {title && (
                    <DialogPrimitive.Title className="text-lg font-semibold tracking-tight text-foreground">
                      {title}
                    </DialogPrimitive.Title>
                  )}
                  {description && (
                    <DialogPrimitive.Description className="text-sm text-muted-foreground">
                      {description}
                    </DialogPrimitive.Description>
                  )}
                </div>
                {showCloseButton && (
                  <DialogPrimitive.Close className="rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
                    <X size={20} />
                    <span className="sr-only">Close</span>
                  </DialogPrimitive.Close>
                )}
              </div>
            )}

            {/* Content */}
            <div className="min-h-0 overflow-y-auto p-4 sm:p-6">{children}</div>
          </DialogPrimitive.Content>
        </div>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default Modal;
